import { useState } from "react";
import { errorMessage, rotateToken } from "../api";
import type { ServerDoc } from "../types";
import type { ToastState } from "./Toast";
import CopyButton from "./CopyButton";

interface Props {
  /** The project this server belongs to -- rotation is per (member, project) now. */
  projectId: string;
  server: (ServerDoc & { id: string }) | null;
  /** The raw bearer token, from `ensureServer`. Null only for pre-encryption servers. */
  token: string | null;
  onToken: (token: string | null) => void;
  /**
   * The server's pre-registered OAuth client, also from `ensureServer`. For configs
   * that take a pasted client id + secret (claude.ai's custom connector advanced
   * settings) rather than registering themselves. Null only while loading.
   */
  oauthClient: { id: string; secret: string } | null;
  activeCount: number;
  toolCount: number;
  notify: (message: string, kind?: ToastState["kind"]) => void;
}

export default function ServerPanel({
  projectId,
  server,
  token,
  onToken,
  oauthClient,
  activeCount,
  toolCount,
  notify,
}: Props) {
  const [rotating, setRotating] = useState(false);
  const [tab, setTab] = useState<InstallTab>("claude-code");

  if (!server) return null;

  const url = `${window.location.origin}/mcp/${server.id}`;
  // The token is stored encrypted and handed back by ensureServer, so the snippets below
  // are copy-paste-ready on every visit. A server minted before that was true has no
  // envelope to open, and all we can show for it is the prefix -- rotating fixes it.
  const shown = token ?? `${server.tokenPrefix}…`;
  const hasToken = Boolean(token);

  async function handleRotate() {
    if (
      !confirm(
        "Rotate the token?\n\nThe current token stops working immediately and every " +
          "MCP client using it must be updated with the new one. Clients connected by " +
          "OAuth (Claude in Anthropic's cloud) are signed out and will ask you to " +
          "approve them again."
      )
    ) {
      return;
    }

    setRotating(true);
    try {
      const res = await rotateToken({ projectId });
      onToken(res.data.token);
      notify("New token issued. Update any MCP client using the old one.");
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setRotating(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>Your MCP server</h2>
          <p className="section-sub">
            Set this up once in your AI client. This URL and key are yours alone, for
            this project — teammates each get their own, so personal connections never
            mix.
          </p>
        </div>
        <div className="stats">
          <div className="stat">
            <span className="stat-value">{activeCount}</span>
            <span className="stat-label">services live</span>
          </div>
          <div className="stat">
            <span className="stat-value">{toolCount}</span>
            <span className="stat-label">tools exposed</span>
          </div>
          <div className="stat">
            <span className="stat-value">{server.callCount ?? 0}</span>
            <span className="stat-label">calls</span>
          </div>
        </div>
      </div>

      <div className="field">
        <label>Endpoint</label>
        <div className="field-row">
          <code className="code-inline">{url}</code>
          <CopyButton value={url} label="Copy URL" />
        </div>
      </div>

      <div className="field">
        <label>Bearer token</label>
        <div className="field-row">
          <code className={`code-inline ${hasToken ? "" : "muted"}`}>{shown}</code>
          {hasToken && <CopyButton value={token!} label="Copy token" />}
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleRotate}
            disabled={rotating}
          >
            {rotating ? "Rotating…" : "Rotate"}
          </button>
        </div>
        {hasToken ? (
          <p className="field-note">
            Stored encrypted, so it is filled into the commands below for you. Rotating
            issues a new one and immediately invalidates the old.
          </p>
        ) : (
          <p className="field-note warn">
            This server predates encrypted token storage, so its token cannot be shown —
            only a hash of it was kept. Rotate to get one that can, then update any MCP
            client using the old token.
          </p>
        )}
      </div>

      {oauthClient && (
        <div className="field">
          <label>OAuth client</label>
          <div className="field-row">
            <code className="code-inline">{oauthClient.id}</code>
            <CopyButton value={oauthClient.id} label="Copy client ID" />
          </div>
          <div className="field-row">
            <code className="code-inline">{oauthClient.secret}</code>
            <CopyButton value={oauthClient.secret} label="Copy secret" />
          </div>
          <p className="field-note">
            For clients that ask for OAuth credentials up front — in claude.ai, add the
            endpoint above as a custom connector and paste these under Advanced
            settings. Most clients (including Claude Code) register themselves
            automatically and need neither; you approve them here on first connect
            either way.
          </p>
        </div>
      )}

      <div className="field">
        <label>Install</label>
        <div className="tabs">
          {(
            [
              ["claude-code", "Claude Code"],
              ["claude-cloud", "Claude (cloud)"],
              ["claude-desktop", "Claude Desktop"],
              ["curl", "curl"],
            ] as const
          ).map(([id, name]) => (
            <button
              key={id}
              className={`tab ${tab === id ? "tab-active" : ""}`}
              onClick={() => setTab(id)}
            >
              {name}
            </button>
          ))}
        </div>

        <div className="snippet">
          <pre>
            <code>{snippet(tab, url, token)}</code>
          </pre>
          <CopyButton value={snippet(tab, url, token)} label="Copy" subtle />
        </div>

        {tab === "claude-code" && isWindows() && (
          <p className="field-note">
            On Windows the token goes in the URL, because PowerShell cannot pass a JSON
            argument through to <code>claude</code> intact. It authenticates exactly the
            same way — but it will sit in your shell history, so prefer the Claude Desktop
            config or run the macOS/Linux command from Git Bash if that matters to you.
          </p>
        )}

        {tab === "claude-cloud" && (
          <p className="field-note">
            No token in the config: environments like Claude Code on the web can't carry
            one, so the client connects with OAuth instead. On first use it sends you
            here to approve access — sign in with the same account as this dashboard.
            Its tokens are short-lived and refresh on their own, and rotating the token
            above signs OAuth clients out too. If the client asks for a client ID and
            secret (claude.ai&rsquo;s custom connector does, under Advanced settings),
            use the OAuth client shown above.
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * Which Claude Code install command works depends on the shell, and the difference is not
 * cosmetic -- the obvious command is silently wrong.
 *
 * `claude mcp add --transport http … --header "Authorization: Bearer …"` is what the CLI's
 * own help suggests, and it *drops the header on the floor*: the flag is documented as
 * "WebSocket headers" and never reaches an HTTP server's config. The add reports success,
 * the server is registered with no credential, and every request 401s. Do not put it back.
 *
 * `mcp add-json` does persist headers, but PowerShell strips the inner double quotes out of
 * a JSON argument on its way to a native command, so `claude` receives `{type:http,…}` and
 * rejects it. Escaping them doesn't help -- the argument then splits on the space in
 * "Bearer <token>" -- and the CLI's parser rejects `--%`. There is no PowerShell spelling.
 *
 * So Windows gets the `?key=` query parameter instead, which `mcp/auth.ts` accepts for
 * exactly this reason: no header, no JSON, nothing for a shell to mangle.
 */
function isWindows(): boolean {
  return /win/i.test(navigator.platform || navigator.userAgent);
}

type InstallTab = "claude-code" | "claude-cloud" | "claude-desktop" | "curl";

function snippet(tab: InstallTab, url: string, token: string | null): string {
  const t = token ?? "YOUR_TOKEN";

  switch (tab) {
    case "claude-code":
      if (isWindows()) {
        return `claude mcp add --transport http mochi "${url}?key=${t}"`;
      }
      return `claude mcp add-json mochi '${JSON.stringify({
        type: "http",
        url,
        headers: { Authorization: `Bearer ${t}` },
      })}'`;

    case "claude-cloud":
      // URL only, on purpose: the client 401s, discovers our OAuth server, and sends
      // the user to /consent. This is the config for anywhere a static token cannot
      // live -- Claude Code in Anthropic's cloud (.mcp.json in the repo), claude.ai
      // custom connectors, or any machine where you'd rather sign in than paste.
      return [
        `# Claude Code, any machine — sign in via OAuth on first use:`,
        `claude mcp add --transport http mochi ${url}`,
        ``,
        `# Claude Code on the web / Anthropic cloud reads .mcp.json from the repo:`,
        JSON.stringify(
          { mcpServers: { mochi: { type: "http", url } } },
          null,
          2
        ),
      ].join("\n");

    case "claude-desktop":
      // Desktop's config file takes the same shape as any Streamable HTTP server.
      return JSON.stringify(
        {
          mcpServers: {
            mochi: {
              type: "http",
              url,
              headers: { Authorization: `Bearer ${t}` },
            },
          },
        },
        null,
        2
      );

    case "curl":
      return `curl -sS ${url} \\\n  -H "Authorization: Bearer ${t}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`;
  }
}
